import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { Control, UseFormClearErrors, UseFormGetValues, UseFormSetError, UseFormSetValue, UseFormUnregister } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, FieldArray, formValueSelector } from 'redux-form';

import type * as ResourceStatusesViewModel from 'ab-viewModels/resources/resourceStatuses.viewModel';
import type { SkillViewModel } from 'ab-viewModels/skill.viewModel';
import type UnionTypeVM from 'ab-viewModels/unionType/unionType.viewModel';
import type { WageRateVM } from 'ab-viewModels/wageRate/wageRate.viewModel';

import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { CDLStatus, CDLStatusLabel } from '@acceligentllc/shared/enums/cdlStatus';

import type { RootState } from 'af-reducers';

import { DEFAULT_EMPLOYEE_IMAGE } from 'ab-common/constants/value';

import Checkbox from 'af-fields/Checkbox';
import Dropdown from 'af-fields/Dropdown';
import type { OwnProps as MultiTagSelectProps } from 'af-fields/MultiTagSelect';
import MultiTagSelect from 'af-fields/MultiTagSelect';
import Select from 'af-fields/SelectField';
import TextField from 'af-fields/Text';

import ImageTag from 'af-components/Image';
import SkillMultiValueLabel from 'af-components/SharedForms/Resources/Shared/SkillMultiValueLabel';
import SkillOption from 'af-components/SharedForms/Resources/Shared/SkillOption';

import * as EmployeeStatusActions from 'af-actions/employeeStatus';
import * as SkillActions from 'af-actions/skill';
import * as UnionTypeActions from 'af-actions/unionType';
import * as WageRateActions from 'af-actions/wageRate';

import EmployeeCredentialForm from './EmployeeCredentialForm';
import type { CredentialFM, CredentialFormValues, EmployeeFM } from './formModel';
import * as Helpers from './helpers';
import styles from './styles.module.scss';

interface OwnProps extends InjectedFormProps<EmployeeFM> {
	companyName: string;
	onSubmit: (form: EmployeeFM) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control: Control<CredentialFormValues, any, CredentialFormValues>;
	getValues: UseFormGetValues<CredentialFormValues>;
	unregister: UseFormUnregister<CredentialFormValues>;
	setError: UseFormSetError<CredentialFormValues>;
	clearErrors: UseFormClearErrors<CredentialFormValues>;
	setValue: UseFormSetValue<CredentialFormValues>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const DEFAULT_AVAILABLE = Object.freeze({
	id: -1,
	name: 'Available',
	index: 0,
	available: true,
	isDeleted: false,
});

const CDLOptions = Object.keys(CDLStatus).map((_cdlStatus) => ({ name: CDLStatusLabel[_cdlStatus], id: _cdlStatus }));

const getUnionTypeOptionValue = (option: UnionTypeVM) => option.id;
const getUnionTypeOptionLabel = (option: UnionTypeVM) => option.name;

const EmployeeForm: React.FC<Props> = (props) => {
	const {
		change,
		imageUrl,
		findAllStatusesForCompany,
		findAllSkillsForCompanyByQuery,
		findAllWageRatesByQuery,
		findAllUnionTypes,
		control,
		setError,
		unregister,
		getValues,
		clearErrors,
		setValue,
	} = props;

	const [companyEmployeeStatuses, setCompanyEmployeeStatuses] = React.useState<ResourceStatusesViewModel.List>([]);
	const [skillList, setSkillList] = React.useState<SkillViewModel[]>([]);
	const [wageRateList, setWageRateList] = React.useState<WageRateVM[]>([]);
	const [unionTypeList, setUnionTypeList] = React.useState<UnionTypeVM[]>([]);
	const [isLoaded, setIsLoaded] = React.useState(false);

	// On mount
	React.useEffect(() => {
		const initialize = async () => {
			const [
				{ available, unavailable },
				skills,
				wageRates,
				unionTypes,
			] = await Promise.all([
				findAllStatusesForCompany(),
				findAllSkillsForCompanyByQuery(''),
				findAllWageRatesByQuery(''),
				findAllUnionTypes(),
			]);

			setCompanyEmployeeStatuses([DEFAULT_AVAILABLE, ...available, ...unavailable]);
			setSkillList(skills);
			setWageRateList(wageRates);
			setUnionTypeList(unionTypes);
			setIsLoaded(true);
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onWageRateChange = React.useCallback((wageRate: WageRateVM) => {
		if (!wageRate) {
			change('wageRate', null);
		}
	}, [change]);

	const handleChangeCredentials = React.useCallback((next: CredentialFM[]) => {
		change('credentials', next);
	}, [change]);

	if (!isLoaded) {
		return null;
	}

	return (
		<>
			<div className="form-box">
				<Row>
					<div className={styles['section-title']}>
						Personal information
					</div>
				</Row>
				<Row className="row--flex">
					<div className="avatar__container">
						<div className="avatar">
							<ImageTag
								fallbackSrc={DEFAULT_EMPLOYEE_IMAGE}
								minSize={BlobStorageImageSizeContainer.SIZE_200X200}
								src={imageUrl}
								tryOriginal={true}
								tryRoot={true}
							/>
						</div>
					</div>
					<div className="avatar__neighbour">
						<Row className="row--padded-top">
							<Col md={6}>
								<Field
									component={TextField}
									defaultValue="N/A"
									label="Name"
									name="fullName"
								/>
							</Col>
							<Col md={6}>
								<Field
									component={Dropdown}
									disabled={false}
									id="employeeStatusId"
									label="Current Status *"
									name="employeeStatusId"
									options={companyEmployeeStatuses}
									renderMenuItem={Helpers.renderResourceStatusMenuItem}
									valueKey="id"
									withCaret={true}
								/>
							</Col>
							<Col md={6}>
								<Field
									component={TextField}
									defaultValue="N/A"
									label="Mobile Phone"
									name="phoneNumber"
								/>
							</Col>
							<Col md={6}>
								<Field
									component={TextField}
									defaultValue="N/A"
									label="SMS Subscription"
									name="subscriptionStatus"
								/>
							</Col>
						</Row>
						<Row>
							<Col md={6}>
								<Field
									component={TextField}
									defaultValue="N/A"
									label="Location"
									name="locationNickname"
								/>
							</Col>
							<Col md={6}>
								<Field
									component={Dropdown}
									disabled={false}
									id="cdlStatus"
									initialValue="cdlStatus"
									label="CDL Status *"
									labelKey="name"
									name="cdlStatus"
									options={CDLOptions}
									valueKey="id"
									withCaret={true}
								/>
							</Col>
							<Col md={12}>
								<Field
									component={TextField}
									defaultValue="N/A"
									label="E-mail"
									name="email"
								/>
							</Col>
						</Row>
						<Row>
							<Col md={6}>
								<Field
									component={Select}
									filterOption={Helpers.filterWageRates}
									formatOptionLabel={Helpers.formatWageRateOptionLabel}
									getOptionLabel={Helpers.getWageRateOptionLabel}
									getOptionValue={Helpers.getWageRateOptionValue}
									label="Labor type *"
									name="wageRate"
									onValueChange={onWageRateChange}
									options={wageRateList}
									placeholder="Enter Labor Type"
								/>
							</Col>
							<Col md={6}>
								<Field
									component={Select}
									getOptionLabel={getUnionTypeOptionLabel}
									getOptionValue={getUnionTypeOptionValue}
									label="Union type"
									name="unionType"
									options={unionTypeList}
									placeholder="Select Union Type"
								/>
							</Col>
							<Col md={12}>
								<Field
									component={Checkbox}
									grouped={true}
									label="Show on schedule board"
									name="showOnScheduleBoard"
								/>
							</Col>
						</Row>
					</div>
				</Row>
				<Row>
					<Col md={24}>
						<FieldArray<MultiTagSelectProps<SkillViewModel>>
							component={MultiTagSelect}
							getOptionLabel={Helpers.getSkillOptionLabel}
							getOptionValue={Helpers.getSkillOptionValue}
							label="Skills"
							MultiValueLabel={SkillMultiValueLabel}
							name="skills"
							Option={SkillOption}
							options={skillList}
							placeholder="Choose skills"
						/>
					</Col>
				</Row>
			</div>
			<div className="form-box">
				<Row>
					<div className={styles['section-title']}>
						Credentials
					</div>
				</Row>
				<Row className={`row ${styles['credential-container']}`}>
					<EmployeeCredentialForm
						clearErrors={clearErrors}
						control={control}
						getValues={getValues}
						onChange={handleChangeCredentials}
						setError={setError}
						setValue={setValue}
						unregister={unregister}
					/>
				</Row>
			</div>
		</>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { imageUrl } = formValueSelector(ownProps.form)(state, 'isStatusAvailable', 'imageUrl');

	return {
		imageUrl,
	};
}

function mapDispatchToProps() {
	return {
		findAllWageRatesByQuery: WageRateActions.findAllByQuery,
		findAllSkillsForCompanyByQuery: SkillActions.findAllForCompanyByQuery,
		findAllStatusesForCompany: EmployeeStatusActions.findAllForCompany,
		findAllUnionTypes: UnionTypeActions.findAll,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(EmployeeForm);
